import React, { type PropsWithChildren, useMemo } from 'react';

import type { BootstrapState } from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import { type BootstrapContainerProps, initialState } from '../../index';
import { StaticBootstrapContext } from '../index';

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	'queryParams' | 'referrer' | 'prevQueryParams'
>;

export const defaultStaticContext = initialState;

export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParams,
}: PropsWithChildren<BootstrapContainerProps>): React.JSX.Element => {
	// Do not change this! This is what enforces only a single rerender when config is ready
	const staticLock = props.isConfigLoaded;

	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		const parsedQueryParams = queryParams && parseQueryParams(queryParams);
		return {
			cloudId: props.cloudId || initialState.cloudId,
			aggregatorHostName: props.aggregatorHostName || initialState.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || initialState.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || initialState.SAINHostName,
			orgId: props.orgId || initialState.orgId,
			primaryProduct: props.primaryProduct || initialState.primaryProduct,
			analyticsAttributes: props.analyticsAttributes || initialState.analyticsAttributes,
			features: props.features || initialState.features,
			intercomHmac: props.intercomHmac || initialState.intercomHmac,
			experimentConfig: props.experimentConfig || initialState.experimentConfig,
			experimentLayers: props.experimentLayers || initialState.experimentLayers,
			isNounsAvailable: props.isNounsAvailable || initialState.isNounsAvailable,
			isRovoEnabled: props.isRovoEnabled || initialState.isRovoEnabled,
			isConfigLoaded: props.isConfigLoaded || initialState.isConfigLoaded,
			nouns: props.nouns || initialState.nouns,
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,
			productEdition: props.productEdition || initialState.productEdition,
			productDataRegions: props.productDataRegions || initialState.productDataRegions,
			rovoFeatures: props.rovoFeatures || initialState.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || initialState.linkedCloudARIs,
			browserHistoryItems: props.browserHistoryItems || initialState.browserHistoryItems,
			onHistoryHeadingClick: props.onHistoryHeadingClick || initialState.onHistoryHeadingClick,
			isJiraIssueIndexed: props.isJiraIssueIndexed || initialState.isJiraIssueIndexed,
			user: props.user || initialState.user,
			searchSessionId: props.searchSessionId || initialState.searchSessionId,
			thirdPartyConfigs: props.thirdPartyConfigs || initialState.thirdPartyConfigs,
			products: props.products || initialState.products,
			initialSpaceARIs: props.initialSpaceARIs || initialState.initialSpaceARIs,
			initialTextQueryParam: parsedQueryParams?.text || initialState.initialTextQueryParam,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [staticLock]);

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			{children}
		</StaticBootstrapContext.Provider>
	);
};
